import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import axios from "axios";
import { Menu, X, ArrowUpRight, Star, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const links = [
  { label: "Home", href: "/", color: "#385244" },
  { label: "Trattamenti", href: "/all-on-4", key: "trattamenti", color: "#B5544E", noNav: true, children: [
    { label: "All on four impianti a carico immediato", href: "/all-on-4" },
    { label: "Implantologia zigomatica", href: "/implantologia-zigomatica" },
    { label: "Implantologia iuxtaossea", href: "/implantologia-iuxtaossea" },
    { label: "Implantologia in carenza di osso", href: "/implantologia-in-carenza-di-osso" },
    { label: "Rialzo del seno mascellare", href: "/rialzo-del-seno-mascellare" },
    { label: "Carico immediato All on Four", href: "/carico-immediato-all-on-four" },
    { label: "Protesi fissa e faccette in zirconia ceramica", href: "/protesi-e-faccette" },
  ] },
  { label: "Articoli di approfondimento", href: "/blog", color: "#B37A5B" },
  { label: "Testimonianze", href: "/testimonianze", color: "#705248" },
  { label: "Chi siamo", href: "/chi-siamo", color: "#4A4E8E" },
  { label: "Dove siamo", href: "/dove-siamo", key: "dove-siamo", color: "#31708E", children: [
    { label: "Impianti dentali provincia di Milano e Monza Brianza", href: "/impianti-dentali-milano" },
    { label: "Impianti dentali Novara", href: "/impianti-dentali-novara" },
    { label: "Impianti dentali Chivasso (TO)", href: "/impianti-dentali-torino" },
    { label: "Impianti dentali Seriate (Bergamo)", href: "/impianti-dentali-bergamo" },
    { label: "Impianti dentali Palermo", href: "/impianti-dentali-palermo" },
  ] },
  { label: "Contatti", href: "/contatti", color: "#15201A" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(null);
  const [google, setGoogle] = useState(null);
  const lenis = useLenis();
  const headerRef = useRef(null);

  const goTo = (e, href) => {
    if (!href.startsWith("#")) { setOpen(false); return; }
    const el = document.querySelector(href);
    if (!el) {
      e.preventDefault();
      setOpen(false);
      window.location.href = "/" + href;
      return;
    }
    e.preventDefault();
    setOpen(false);
    if (lenis) lenis.scrollTo(el, { offset: -((headerRef.current?.offsetHeight || 160) + 16), duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    axios.get(`${API}/google-reviews`).then(({ data }) => setGoogle(data)).catch(() => {});
    const el = headerRef.current;
    const setH = () => {
      if (!el) return;
      const menu = el.querySelector("[data-testid=mobile-menu]");
      document.documentElement.style.setProperty("--header-h", `${el.offsetHeight - (menu ? menu.offsetHeight : 0)}px`);
    };
    setH();
    const ro = new ResizeObserver(() => requestAnimationFrame(setH));
    if (el) ro.observe(el);
    return () => { window.removeEventListener("scroll", onScroll); ro.disconnect(); };
  }, []);

  return (
    <motion.header
      ref={headerRef}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#EAF4F8]/80 border-b border-border" : "bg-[#EAF4F8]/60 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="w-full px-4 sm:px-6 md:px-12 h-24 sm:h-32 flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink min-w-0" data-testid="header-logo">
          <a href="#top" className="relative flex-shrink-0">
            <img
              src="/logo-gsi.png"
              alt="Gruppo Stomatologico Italiano"
              className="h-14 w-14 sm:h-24 sm:w-24 object-contain"
            />
            <span className="absolute -top-0.5 -right-1.5 text-[9px] sm:text-xs font-bold text-[#15201A]" aria-label="Marchio registrato">®</span>
          </a>
          <span className="flex flex-col leading-tight min-w-0">
            <span className="text-[11px] sm:text-base uppercase tracking-[0.06em] sm:tracking-[0.12em] font-extrabold text-[#15201A] whitespace-nowrap">Gruppo Stomatologico Italiano</span>
            <span className="font-serif-display italic text-xs sm:text-lg text-[#385244] mt-0.5 whitespace-nowrap">"sempre al Tuo fianco"</span>
            <span className="mt-0.5 sm:mt-1 flex items-baseline gap-1.5 sm:gap-2 whitespace-nowrap" data-testid="header-tollfree">
              <span className="text-[8px] sm:text-sm font-bold uppercase tracking-wide text-[#C98E6E]">Numero verde gratuito</span>
              <a href="tel:800628192" className="text-base sm:text-3xl font-extrabold text-[#C98E6E] hover:text-[#B37A5B] transition-colors duration-200 leading-none">800 628 192</a>
              {google?.rating && (
                <a
                  href="#recensioni"
                  className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-white/90 border border-border px-3 py-1 ml-1 hover:border-[#385244] transition-colors duration-200"
                  data-testid="header-google-badge"
                  title={`${google.rating} su 5 · ${google.review_count} recensioni Google`}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <Star size={13} className="text-[#FBBC05] fill-[#FBBC05]" />
                  <span className="text-xs font-extrabold text-[#15201A] leading-none">{String(google.rating).replace(".", ",")}</span>
                  <span className="text-xs text-[#5C6B61] leading-none">· {google.review_count} recensioni</span>
                </a>
              )}
            </span>
          </span>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <span className="hidden 2xl:flex items-baseline gap-1 whitespace-nowrap" data-testid="header-wordmark">
            <span className="font-serif-display text-2xl text-[#15201A]">Dentista</span>
            <span className="font-serif-display italic text-2xl text-[#385244]">Sostenibile</span>
          </span>
          <a
            href="/contatti"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border-2 border-[#385244] text-[#385244] hover:bg-[#385244] hover:text-white text-[15px] xl:text-base font-bold px-6 py-3 transition-colors duration-300 active:scale-95 whitespace-nowrap"
            data-testid="header-cta-btn"
          >
            Chiedi informazioni
            <ArrowUpRight size={18} />
          </a>
          <button
            className="xl:hidden p-2 text-[#15201A]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={30} /> : <Menu size={30} />}
          </button>
        </div>
      </div>
      <nav className="relative overflow-x-auto md:overflow-visible no-scrollbar border-t border-border/60" data-testid="main-nav-strip">
        <div className="flex items-center gap-2 md:gap-2.5 xl:gap-3 py-2.5 px-4 sm:px-6 w-max md:w-full md:flex-wrap md:justify-center xl:max-w-7xl mx-auto">
          {links.map((l) => {
            const isOpen = drop === l.key;
            const Tag = l.noNav ? "button" : "a";
            return (
              <div key={l.href} className="relative group" onMouseLeave={() => isOpen && setDrop(null)}>
                <Tag
                  href={l.noNav ? undefined : l.href}
                  type={l.noNav ? "button" : undefined}
                  onClick={(e) => {
                    if (!l.children || !l.noNav) return goTo(e, l.href);
                    e.preventDefault();
                    if (window.innerWidth < 768) setOpen(true);
                    else setDrop(isOpen ? null : l.key);
                  }}
                  className="inline-flex items-center gap-1 text-[12px] xl:text-[13px] font-bold whitespace-nowrap text-white rounded-full px-3.5 py-1.5 xl:px-4 hover:opacity-85 hover:-translate-y-px transition-[opacity,transform] duration-200"
                  style={{ backgroundColor: l.color }}
                  data-testid={`strip-nav-link-${l.key || l.href.slice(1)}`}
                  aria-haspopup={l.children ? "menu" : undefined}
                  aria-expanded={l.children ? isOpen : undefined}
                >
                  {l.label}
                  {l.children && <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                </Tag>
                {l.children && (
                  <div className={`${isOpen ? "block" : "hidden md:group-hover:block"} absolute left-0 top-full pt-2 z-50`} data-testid={`nav-dropdown-${l.key}`}>
                    <div className="bg-white border border-border rounded-2xl shadow-xl p-2 min-w-[260px]">
                      {l.children.map((c) => (
                        <a key={c.href} href={c.href} className="block rounded-xl px-4 py-2.5 text-sm font-semibold text-[#15201A] hover:bg-[#EAF4F8] hover:text-[#385244]" data-testid={`nav-sub-link-${c.href.slice(1)}`}>
                          {c.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>
      {!open && <div className="md:hidden pointer-events-none absolute right-0 bottom-0 h-[52px] w-14 bg-gradient-to-l from-[#EAF4F8] to-transparent" aria-hidden="true" />}
      {open && (
        <div className="xl:hidden bg-[#EAF4F8] border-t border-border px-6 py-6 flex flex-col gap-5 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          {links.map((l) => (
            <div key={l.href} className="flex flex-col gap-3">
              {l.noNav ? (
                <p className="inline-flex items-center gap-3 text-lg font-semibold text-[#15201A]" data-testid={`mobile-nav-label-${l.key}`}>
                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                  {l.label}
                </p>
              ) : (
                <a
                  href={l.href}
                  onClick={(e) => goTo(e, l.href)}
                  className="inline-flex items-center gap-3 text-lg font-semibold text-[#15201A]"
                  data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                >
                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                  {l.label}
                </a>
              )}
              {l.children && l.children.map((c) => (
                <a key={c.href} href={c.href} className="ml-6 text-base font-medium text-[#3F4C44]" data-testid={`mobile-sub-link-${c.href.slice(1)}`}>
                  {c.label}
                </a>
              ))}
            </div>
          ))}
          <a
            href="/contatti"
            className="inline-flex justify-center rounded-full bg-[#385244] text-white font-bold px-6 py-3"
            data-testid="mobile-cta-btn"
          >
            Chiedi informazioni
          </a>
        </div>
      )}
    </motion.header>
  );
};
